import { EnvType } from '@blk/aw-api-clients';
import { isCloud } from '@blk/aw-env-detection';
import { isEmpty, kebabCase } from 'lodash';

const DEBUG_ON_QUERY_STRING = 'debugOn';

export const getDebugOnFromUrl = (): string | undefined => getUrlParam(DEBUG_ON_QUERY_STRING) || undefined;

export enum AW_URL_QUERY_PARAM {
  // URL parameter for the act-as mode when acting as another advisor
  ACT_AS_MODE = 'actAsMode',
  ANALYSIS_DATE = 'analysisDate',
  // URL parameter to get the analysis benchmark id used in benchmarks
  ANALYSIS_BENCHMARK = 'analysisBenchmark',
  // URL parameter for handling scenario when my book toggle is configured
  BOOK = 'book',
  // URL parameter which contains code when redirected by third party authorization mechanism (e.g. PKCE)
  CODE = 'code',
  // query param to identify the action to be performed in comparison page
  COMPARISON_ACTION = 'comparisonAction',
  // This query parameter is related to ids that is being used in comparison
  COMPARISON_IDS = 'comparisonIds',
  CONFIGURATION_DIRECTORY = 'configurationDirectory',
  // URL parameter to enable content draft mode - required to add decorated elements for universal editor
  CONTENT_MODE = 'contentMode',
  // URL parameter to optionally override content scope - allows displaying content from PUBLIC scope for editing purposes
  CONTENT_SCOPE = 'contentScope',
  // URL parameter for containing error information when redirected by third party authorization mechanism (e.g. PKCE)
  ERROR = 'error',
  // URL parameter for the view-as id when the user first selects a view-as name
  ID = 'id',
  // This is a temporary hack to support copy/pasted paths with Oauth 2.0 where the hash is stripped out after login
  LOCATION = 'location',
  // URL parameter for the view-as login when the user first selects a view-as name
  LOGIN = 'login',
  // URL parameter for hiding the package selector when mode is set to 'DEMO'
  MODE = 'mode',
  // This query parameter is related to external client page filter integration
  PAGE = 'p',
  PKG = 'pkg',
  // This query parameter is related to id that is being used in partner portfolio deep linking
  PORTFOLIOS = 'portfolios',
  //This query parameter is related to status change of the proposal
  PROPOSAL_CHANGE_STATE = 'proposalChangeState',
  // URL parameter which contains state when redirected by third party authorization mechanism (e.g. PKCE)
  STATE = 'state',
  // URL parameter for handling workflow when an error is returned by third party authorization mechanism (e.g. PKCE)
  ERROR_DESCRIPTION = 'error_description',
  // URL parameter for the view-as name when the user first selects a view-as name
  VIEW_AS = 'viewas',
  // URL parameter when loading deep-dive from alerts
  WORKFLOW_DATE = 'workflowDate',
  /**
   * @deprecated to be removed
   * URL parameter to check if current tab is in shopping-cart mode
   */
  SHOPPING_CART_MODE = 'portfolioSelection',

  // URL parameter for the comparison currency of the Portfolio Analyzer
  COMPARISON_CURRENCY = 'currency',
  // URL parameter to indicate environment switch (UAT/PROD) with the new package name
  ENV_SWITCH = 'envSwitch',
}

/**
 * List of controlled query parameters that are eligible for list pattern
 */
export enum AW_URL_QUERY_LIST_PARAM {
  FILTER = 'f',
}

export const AW_URL_QUERY_LIST_PARAM_VALUE_SET = new Set<string>(Object.values(AW_URL_QUERY_LIST_PARAM));

export const isAwUrlQueryListParam = (paramName: string): paramName is AW_URL_QUERY_LIST_PARAM =>
  AW_URL_QUERY_LIST_PARAM_VALUE_SET.has(paramName);

export const getAwUrlParam = (awUrlQueryParam: AW_URL_QUERY_PARAM): string => getUrlParam(awUrlQueryParam);

// If set, the app is being used in demo mode
export const DEMO_MODE = 'DEMO';

export const isDemoMode = (): boolean => getAwUrlParam(AW_URL_QUERY_PARAM.MODE) === DEMO_MODE;

/**
 * Maximum accepted length for a configuration directory value. Legitimate values are path-like
 * identifiers (e.g. `one/two`, `/d0/prod1/reports/workspace/dextga1`), which can be deeply nested.
 */
const MAX_CONFIGURATION_DIRECTORY_LENGTH = 500;

/**
 * A configuration directory is a server-side path identifier composed of one or more segments of
 * alphanumeric characters, hyphens, and underscores, separated by single forward slashes with an
 * optional leading slash. This allowlist rejects any value that could redirect configuration,
 * localization, or document requests to an untrusted location — protocol/scheme separators (`:`),
 * scheme-relative prefixes (`//`), userinfo (`@`), path traversal (`..`), backslashes, encoded
 * bypasses, whitespace, and injection characters all fail the pattern.
 */
const CONFIGURATION_DIRECTORY_SAFE_PATTERN = /^\/?[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/;

/**
 * Validates that a caller-controlled configuration directory value is a safe, path-like identifier
 * (CWE-15/CWE-610). Normalizes by stripping trailing slashes before validation. Returns false for
 * anything that is not a strict allowlist match so callers can fall back to the trusted default behaviour.
 *
 * @param value - The raw configuration directory string (potentially user-controlled)
 * @returns true when the value is a safe configuration directory, false otherwise
 */
export const isSafeConfigurationDirectory = (value: string): boolean => {
  const normalized = value.replace(/\/+$/, '');
  return (
    normalized.length <= MAX_CONFIGURATION_DIRECTORY_LENGTH && CONFIGURATION_DIRECTORY_SAFE_PATTERN.test(normalized)
  );
};

export const getConfigurationDirectoryFromUrl = (): string | undefined => {
  const rawConfigDir = getAwUrlParam(AW_URL_QUERY_PARAM.CONFIGURATION_DIRECTORY) || undefined;
  if (rawConfigDir && !isSafeConfigurationDirectory(rawConfigDir)) {
    return undefined;
  }
  return rawConfigDir;
};

// looking for ?contentMode=draft or ?contentMode=preview to enable edit/preview mode for universal editor
export const DRAFT_MODE = 'draft';
export const PREVIEW_MODE = 'preview';
export const isDraftMode = (): boolean => getAwUrlParam(AW_URL_QUERY_PARAM.CONTENT_MODE) === DRAFT_MODE;
export const isPreviewMode = (): boolean => getAwUrlParam(AW_URL_QUERY_PARAM.CONTENT_MODE) === PREVIEW_MODE;

// looking for ?contentScope=public to display/edit content from PUBLIC scope
export const CONTENT_PUBLIC_SCOPE = 'public';
export const contentScope = (): string => getAwUrlParam(AW_URL_QUERY_PARAM.CONTENT_SCOPE);
export const isPublicScope = (): boolean => getAwUrlParam(AW_URL_QUERY_PARAM.CONTENT_SCOPE) === CONTENT_PUBLIC_SCOPE;

type UrlParams = {
  [propName: string]: string;
} & {
  [L in AW_URL_QUERY_LIST_PARAM]?: string[];
};

export const evaluateConfigurationDirectory = (
  configDirUrlParam: string | undefined,
  defaultConfigDir: string | undefined,
  envType: EnvType
): string | undefined => {
  if (configDirUrlParam && isSafeConfigurationDirectory(configDirUrlParam)) {
    return configDirUrlParam;
  } else if (envType === EnvType.BETA || envType === EnvType.UAT) {
    return defaultConfigDir;
  }

  return undefined;
};

/**
 * Finds env type from second part of path name if first part is apps otherwise from first part of path name
 */
export const getEnvironmentType = (urlPathName: string): EnvType => {
  const forceEnv = getUrlParam('forceEnv') || process.env.VITE_EXTERNAL_API_OVERRIDE;
  if (forceEnv === EnvType.BETA || forceEnv === EnvType.UAT) {
    return forceEnv;
  }

  const splitUrl = urlPathName.split('/');
  const pathToEvaluate = !!(splitUrl[1] === 'apps') ? splitUrl[2] || '' : splitUrl[1] || '';
  if (/beta/i.exec(pathToEvaluate)) {
    // Edge case: advisor-center-360-beta in cloud environment should be treated as UAT
    if (isCloud() && /advisor-center-360-beta/i.exec(pathToEvaluate)) {
      return EnvType.UAT;
    }
    return EnvType.BETA;
  } else if (/uat/i.exec(pathToEvaluate)) {
    return EnvType.UAT;
  } else if (/\bqa\b/i.exec(pathToEvaluate)) {
    return EnvType.QA;
  }

  return EnvType.DEFAULT;
};

export function getUrlParam(paramName: string): string {
  // https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName) ?? '';
}

export const insertAwUrlQueryParam = (
  awUrlQueryParam: AW_URL_QUERY_PARAM,
  value: string,
  useReplaceState: boolean = false
): void => {
  insertUrlQueryParam(awUrlQueryParam, value, useReplaceState);
};

export function insertUrlQueryParam(key: string, value: string, useReplaceState: boolean = false): void {
  const urlParams = parseUrlForParams();
  if (urlParams[key] === value) {
    return;
  }

  if (urlParams[key]) {
    Reflect.deleteProperty(urlParams, key);
  }
  const queryString = urlParamsToQuery(urlParams);
  const urlAppPath = getUrlAppPath();
  const newUrl = `?${encodeURIComponent(key)}=${encodeURIComponent(value)}${queryString}${urlAppPath}`;

  if (useReplaceState) {
    window.history.replaceState({}, '', newUrl);
  } else {
    window.history.pushState({}, '', newUrl);
  }
}

export function resetUrlWithParams(urlParams: UrlParams, pathName?: string) {
  const queryString = urlParamsToQuery(urlParams);
  // Sanitize pathName to prevent XSS - encode any potentially harmful characters
  const safePath = pathName ? encodeURI(pathName) : '';
  const newUrl = `?${queryString}${safePath}`;
  window.history.pushState({}, '', newUrl);
}

const deleteKeysAndPushState = (urlParams: UrlParams, keys: string[]): void => {
  const removed = keys.filter((key) => {
    if (urlParams[key] === undefined) {
      return false;
    }
    Reflect.deleteProperty(urlParams, key);
    return true;
  });

  if (removed.length === 0) {
    return;
  }

  const queryString = urlParamsToQuery(urlParams).replace(/^&+/, '');

  // Decode first to handle already-encoded values, then encode to ensure safety
  const hash = window.location.hash;
  let safeHash = '';
  if (hash) {
    try {
      // Decode if already encoded, then re-encode for consistent sanitization
      safeHash = encodeURI(decodeURI(hash));
    } catch (e) {
      // If decode fails (malformed URI), encode as-is
      safeHash = encodeURI(hash);
    }
  }

  // Build URL as relative path starting with '?' to preserve pathname
  const newUrl = queryString ? `?${queryString}${safeHash}` : `?${safeHash}`;

  window.history.pushState({}, '', newUrl);
};

export const removeUrlQueryParam = (key: string): void => {
  const urlParams = parseUrlForParams();
  deleteKeysAndPushState(urlParams, [key]);
};

export const removeUrlQueryParams = (keys: string[]): void => {
  const urlParams = parseUrlForParams();
  deleteKeysAndPushState(urlParams, keys);
};

export const removeAwUrlQueryParam = (awUrlQueryParam: AW_URL_QUERY_PARAM): void => {
  removeUrlQueryParam(awUrlQueryParam);
};

export const removeAwUrlQueryParams = (awUrlQueryParams: AW_URL_QUERY_PARAM[]): void => {
  removeUrlQueryParams(awUrlQueryParams);
};

export function getUrlAppPath(): string {
  let currentPath: string = '';
  let match,
    pl = /\+/g, // Regex for replacing addition symbol with a space
    search = /(#\/(.*)$)/g,
    decode = function (s: string) {
      return decodeURIComponent(s.replace(pl, ' '));
    },
    query = window.location.href;

  while ((match = search.exec(query))) {
    currentPath = decode(match[0]);
  }

  return currentPath;
}

export function urlParamsToQuery(urlParams: UrlParams) {
  return Object.keys(urlParams).reduce((total, paramName) => {
    if (isAwUrlQueryListParam(paramName)) {
      (urlParams[paramName] ?? []).forEach((value) => {
        total = total.concat(`&${encodeURIComponent(paramName)}=${encodeURIComponent(value)}`);
      });
    } else {
      total = total.concat(`&${encodeURIComponent(paramName)}=${encodeURIComponent(urlParams[paramName])}`);
    }
    return total;
  }, '');
}

export function parseUrlForParams(): UrlParams {
  // https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
  const urlParams = new URLSearchParams(window.location.search);
  /*
   * This will deduplicate the query params the same way as previous implementation
   * The last query param will be the one that is used
   */
  const params: UrlParams = Object.fromEntries(urlParams.entries());
  // In the previous step, list pattern will be falsy deduplicated. This step is to correctly capture them and convert the list pattern to array
  Object.values(AW_URL_QUERY_LIST_PARAM).forEach((queryParam) => {
    const values = urlParams.getAll(queryParam);
    if (!isEmpty(values)) {
      params[queryParam] = values;
    }
  });
  return params;
}

export function resolveAssetPath(path: string, base: string) {
  /*
   * removing trailing slash on base to avoid double slash
   * We need a special function to do this because URL does not yet support relative path: https://github.com/whatwg/url/issues/531
   */
  const trimmedBase = base.replace(/\/$/, '');
  const trimmedPath = path.replace(/^\//, '');
  return trimmedBase + '/' + trimmedPath;
}

function shortenToMaxTotal(maxLength: number = 30) {
  return function (maybeUrl: string) {
    if (maybeUrl.length < maxLength) {
      return maybeUrl;
    }
    try {
      const url = new URL(maybeUrl);
      const { pathname: rawPathname, host: rawHost } = url;
      const host = rawHost.replace('www.', '');
      const pathname = rawPathname === '/' ? '' : rawPathname;

      if (!pathname) {
        // only host is of concern, shorten only when it's longer than maxLength
        return host.length > maxLength ? `${host.slice(0, maxLength)}...` : host;
      }

      if (maxLength < host.length) {
        // when maxLength is shorter than host, drop pathname anyway
        return `${host.slice(0, maxLength)}...`;
      }

      if (pathname.length + host.length <= maxLength) {
        return `${host}${pathname}`;
      }
      /*
       * implied: host <= maxLength < host + path
       * keep entire host, shorten pathname to remaining length
       */
      return `${host}...${pathname.slice(host.length - maxLength)}`;
    } catch (e) {
      /* istanbul ignore next */
      return `${maybeUrl.slice(0, maxLength)}...`;
    }
  };
}

function shortenTail(maxTail: number) {
  return function (maybeUrl: string) {
    try {
      const url = new URL(maybeUrl);
      const { pathname, host: rawHost } = url;
      const host = rawHost.replace('www.', '');
      if (pathname.length <= maxTail) {
        return pathname !== '/' ? `${host}${pathname}` : host;
      }
      return pathname !== '/' ? `${host}...${pathname.slice(-maxTail)}` : host;
    } catch (e) {
      /* istanbul ignore next */
      return maybeUrl;
    }
  };
}

/**
 * This util shortens url from the middle
 * The idea is that when shortening a URL usually the domain and the end of the URL contain more information
 * The util accepts either a maxTotal or a maxTail
 * - preferred: maxTail - shortens to domain + shortend pathname
 * - space limited: maxTotal - shortens to shortened domain + shortened pathname, targeting maxTotal
 *
 * @param maybeUrl string - should provide a string url
 * @param options { maxTail?: number, maxTotal?: number }
 * @returns string shortened URL based on the options give
 */
export function shortenUrl(
  maybeUrl: string,
  { maxTotal, maxTail }: { maxTotal?: number; maxTail?: number } = { maxTail: 12 }
) {
  if (maxTotal) {
    return shortenToMaxTotal(maxTotal)(maybeUrl);
  }
  if (maxTail) {
    return shortenTail(maxTail)(maybeUrl);
  }
  /* istanbul ignore next */
  return maybeUrl;
}

/*
 * This function is used to convert a nested tab structure to a slugified path
 * e.g. "Multi-Level Tab#SubTab#SubSubTab" -> multi-level-tab/subtab/subsubtab
 */
export function slugifyPath(path: string) {
  return path.replaceAll(/#/g, '/').split('/').map(kebabCase).join('/');
}

/**
 * Package identifiers must only contain alphanumeric characters, hyphens, and underscores.
 * This allowlist prevents any character that could contribute to a protocol injection,
 * HTML injection, or script injection payload.
 */
const PKG_SAFE_PATTERN = /^[a-zA-Z0-9_-]{0,100}$/;

/**
 * Sanitizes a raw `pkg` URL parameter for safe use in iframe src construction (CWE-80).
 *
 * Combines two layers:
 *   1. Strict allowlist – rejects any value containing characters outside [a-zA-Z0-9_-].
 *      A valid pkg identifier can never contain protocol separators (`:`), slashes (`/`),
 *      angle brackets, or quotes, so any malicious payload fails here.
 *   2. encodeURIComponent – a scanner-recognized URL sanitizer that de-taints the value
 *      for Veracode and SonarQube taint analysis engines.
 *
 * Returns an empty string when the value fails the allowlist — callers must treat
 * an empty return as "omit the pkg parameter entirely".
 *
 * @param rawPkg - The raw pkg string from the URL (potentially user-controlled)
 * @returns URL-encoded pkg string safe for iframe src, or '' if invalid
 */
export const sanitizePkgForUrl = (rawPkg: string): string => {
  if (!rawPkg || !PKG_SAFE_PATTERN.test(rawPkg)) {
    return '';
  }
  return encodeURIComponent(rawPkg);
};

/**
 * Validates that a URL is safe to use in the DOM to prevent XSS attacks (CWE-80).
 * Only allows http:, https: protocols and rejects URLs with dangerous patterns.
 *
 * Use this for any URL that will be inserted into the DOM (script.src, iframe.src, etc.)
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe for DOM insertion, false otherwise
 *
 * @example
 * ```ts
 * isXssSafeUrl('https://example.com/script.js') // true
 * isXssSafeUrl('http://example.com/script.js') // true
 * isXssSafeUrl('/path/to/script.js') // true
 * isXssSafeUrl('javascript:alert(1)') // false
 * isXssSafeUrl('data:text/javascript,alert(1)') // false
 * isXssSafeUrl('<script>alert(1)</script>') // false
 * ```
 */
export const isXssSafeUrl = (url: string): boolean => {
  // Check for common injection patterns first
  const dangerousPatterns = /[<>'"]/;
  if (dangerousPatterns.test(url)) {
    return false;
  }

  try {
    const parsedUrl = new URL(url, window.location.href);
    // Only allow http and https protocols
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    // If URL parsing fails, reject it as potentially malicious
    return false;
  }
};

/**
 * Checks if the application is running inside an iframe.
 *
 * @returns true if the application is loaded inside an iframe, false otherwise
 *
 * @example
 * ```ts
 * isInsideIframe() // false (when running standalone)
 * isInsideIframe() // true (when embedded in an iframe)
 * ```
 */
export const isInsideIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // If accessing window.top throws an error (cross-origin), we're in an iframe
    return true;
  }
};
